import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import type { DiscoverExploreSearchFormData } from '../../types/discoverExploreSearch.types';
import type {
  TourItem,
  TourSearchContextForSave,
  TourSearchPayload,
  DiscoverTourFilters,
} from '../../../../../types/tour.types';
import { DISCOVER_TOUR_FILTERS_INITIAL } from '../../../../../types/tour.types';
import { useTourSearchMutation } from '../../../../../store/services/ideasApi';
import { getApiErrorMessage } from '../../../../../utils/apiErrorMessage';
import { getTotalPages } from '../../../../../utils/pagination.utils';
import type { DiscoverTourCompany } from '../../../../../types/tour.types';
import { buildTourSearchPayload, extractTourCompanies, mergeTourCompanies, tourItemKey } from '../../../../../utils/tourSearch.utils';
import DiscoverSearchPanel from '../DiscoverSearchPanel/DiscoverSearchPanel';
import DiscoverTabSearchForm from '../DiscoverTabSearchForm/DiscoverTabSearchForm';
import DiscoverFiltersPanel from '../DiscoverFilters/DiscoverIdeasFilters';
import DiscoverFiltersModal from '../DiscoverFiltersModal/DiscoverFiltersModal';
import DiscoverCard from '../DiscoverCard/DiscoverCard';
import DiscoverPagination from '../DiscoverPagination/DiscoverPagination';
import SaveDiscoverModal from '../SaveDiscoverModal/SaveDiscoverModal';
import discoverStyles from '../../Discover.module.scss';

type DiscoverTabProps = {
  isMobile: boolean;
  mobileFiltersOpen: boolean;
  onMobileFiltersOpen: () => void;
  onCloseMobileFilters: () => void;
};

const FILTER_SEARCH_DEBOUNCE_MS = 450;

function shouldRefetchTourResults(prev: DiscoverTourFilters, next: DiscoverTourFilters): boolean {
  if (prev.priceRange.min !== next.priceRange.min || prev.priceRange.max !== next.priceRange.max) {
    return true;
  }
  if (prev.companyIds.length !== next.companyIds.length) {
    return true;
  }
  const nextCompanyIds = new Set(next.companyIds);
  return prev.companyIds.some(id => !nextCompanyIds.has(id));
}

const DiscoverTab: FC<DiscoverTabProps> = ({
  isMobile,
  mobileFiltersOpen,
  onMobileFiltersOpen,
  onCloseMobileFilters,
}) => {
  const [filters, setFilters] = useState<DiscoverTourFilters>(DISCOVER_TOUR_FILTERS_INITIAL);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchResults, setSearchResults] = useState<TourItem[]>([]);
  const [searchCount, setSearchCount] = useState(0);
  const [searchPage, setSearchPage] = useState(1);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [itemToSave, setItemToSave] = useState<TourItem | null>(null);
  const [saveToastMessage, setSaveToastMessage] = useState<string | null>(null);
  const [lastSearchContext, setLastSearchContext] = useState<TourSearchContextForSave | null>(null);
  const [filterCompanies, setFilterCompanies] = useState<DiscoverTourCompany[]>([]);
  const lastFormRef = useRef<DiscoverExploreSearchFormData | null>(null);
  const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [tourSearch, { isLoading: isSearching }] = useTourSearchMutation();

  const companiesForFilters = useMemo(
    () => mergeTourCompanies(filterCompanies, filters.companyIds),
    [filterCompanies, filters.companyIds]
  );

  const totalPages = getTotalPages(searchCount);

  const runTourSearch = useCallback(
    async (payload: TourSearchPayload, page: number, refreshFilterCompanies = false) => {
      setSearchError(null);
      try {
        const result = await tourSearch({ payload, page }).unwrap();
        const items = result.results ?? [];
        setSearchResults(items);
        setSearchCount(result.count ?? 0);
        setSearchPage(page);
        setHasSearched(true);
        if (refreshFilterCompanies) {
          setFilterCompanies(extractTourCompanies(items));
        }
      } catch (error: unknown) {
        setSearchResults([]);
        setSearchCount(0);
        setHasSearched(true);
        if (refreshFilterCompanies) {
          setFilterCompanies([]);
        }
        setSearchError(getApiErrorMessage(error, 'Tour search failed. Please try again.'));
      }
    },

    [tourSearch]
  );

  const searchWithCurrentState = useCallback(
    (form: DiscoverExploreSearchFormData, nextFilters: DiscoverTourFilters, page = 1, refreshFilterCompanies = false) => {
      const payload = buildTourSearchPayload(form, nextFilters);
      void runTourSearch(payload, page, refreshFilterCompanies);
    },
    [runTourSearch]
  );

  const scheduleFilterSearch = useCallback(
    (nextFilters: DiscoverTourFilters) => {
      const form = lastFormRef.current;
      if (!form || !hasSearched) return;
      if (filterDebounceRef.current) {
        clearTimeout(filterDebounceRef.current);
      }

      filterDebounceRef.current = setTimeout(() => {
        searchWithCurrentState(form, nextFilters, 1);
      }, FILTER_SEARCH_DEBOUNCE_MS);
    },

    [hasSearched, searchWithCurrentState]
  );

  const handleFiltersChange = (next: DiscoverTourFilters) => {
    setFilters(next);
    if (shouldRefetchTourResults(filters, next)) {
      scheduleFilterSearch(next);
    }
  };

  const handleSearchSubmit = async (values: DiscoverExploreSearchFormData) => {
    lastFormRef.current = values;
    setLastSearchContext({
      fromLocation: values.from.trim(),
      toLocation: values.to.trim(),
      departDate: values.depart.trim().slice(0, 10) || undefined,
      durationDays: (() => {
        const raw = values.durationDays.trim();
        if (!raw) return undefined;
        const n = Number.parseInt(raw, 10);
        return Number.isFinite(n) ? n : undefined;
      })(),
    });

    const initialFilters = DISCOVER_TOUR_FILTERS_INITIAL;
    setFilters(initialFilters);
    searchWithCurrentState(values, initialFilters, 1, true);
  };

  const handlePageChange = (page: number) => {
    const form = lastFormRef.current;
    if (!form) return;
    searchWithCurrentState(form, filters, page);
  };

  const handleFiltersApply = (next: DiscoverTourFilters) => {
    if (filterDebounceRef.current) {
      clearTimeout(filterDebounceRef.current);
      filterDebounceRef.current = null;
    }

    setFilters(next);
    const form = lastFormRef.current;
    if (form && shouldRefetchTourResults(filters, next)) {
      searchWithCurrentState(form, next, 1);
    }
    onCloseMobileFilters();
  };

  const handleSaveItem = (item: TourItem) => {
    setItemToSave(item);
    setSaveModalOpen(true);
  };

  return (
    <>
      <SaveDiscoverModal
        open={saveModalOpen}
        onClose={() => {
          setSaveModalOpen(false);

          setItemToSave(null);
        }}
        item={itemToSave}
        searchContext={lastSearchContext}
        onSaved={message => setSaveToastMessage(message)}
      />

      {saveToastMessage ? (
        <Alert
          severity="success"
          onClose={() => setSaveToastMessage(null)}
          sx={{ mb: 2 }}
        >
          {saveToastMessage}
        </Alert>
      ) : null}

      <Box className={discoverStyles.searchFormSection}>
        <DiscoverSearchPanel
          isMobile={isMobile}
          sectionTitle="Discover"
          cardTitle="Discover Search"
          onFilterClick={isMobile && hasSearched ? onMobileFiltersOpen : undefined}
        >
          <DiscoverTabSearchForm
            isMobileLayout={isMobile}
            isLoading={isSearching}
            onSubmit={handleSearchSubmit}
          />
        </DiscoverSearchPanel>
      </Box>

      {searchError ? (
        <Alert
          severity="error"
          onClose={() => setSearchError(null)}
          sx={{ mb: 2 }}
        >
          {searchError}
        </Alert>
      ) : null}

      {hasSearched && !searchError ? (
        <Box className={`${discoverStyles.resultsSection} ${isMobile ? discoverStyles.resultsSectionMobile : ''}`}>
          {!isMobile ? (
            <Box className={discoverStyles.filtersColumn}>
              <Box className={discoverStyles.filtersPanel}>
                <DiscoverFiltersPanel
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  companies={companiesForFilters}
                />
              </Box>
            </Box>
          ) : null}

          <Box className={discoverStyles.resultsColumn}>
            {isSearching && searchResults.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography
                  variant="h11"
                  sx={{ color: '#5E6A81' }}
                >
                  Searching tours…
                </Typography>
              </Box>
            ) : searchResults.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography
                  variant="h10Semibold"
                  sx={{ color: '#222B3A' }}
                >
                  No results found
                </Typography>

                <Typography
                  variant="h11"
                  sx={{ color: '#5E6A81', mt: 1 }}
                >
                  Try a different search or fewer filters.
                </Typography>
              </Box>
            ) : (
              searchResults.map((item, index) => (
                <DiscoverCard
                  key={tourItemKey(item, index)}
                  item={item}
                  onSave={handleSaveItem}
                />
              ))
            )}

            <DiscoverPagination
              page={searchPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              disabled={isSearching}
            />
          </Box>
        </Box>
      ) : null}

      {isMobile && mobileFiltersOpen ? (
        <DiscoverFiltersModal
          open
          onClose={onCloseMobileFilters}
          filters={filters}
          onApply={handleFiltersApply}
          companies={companiesForFilters}
        />
      ) : null}
    </>
  );
};

export default DiscoverTab;
